import { GripVertical, Trash2, MessageSquare, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { CalloutBlock } from "@/types/helpArticle";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}

interface MobileReorderProps {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

interface CalloutBlockEditorProps {
  block: CalloutBlock;
  onChange: (block: CalloutBlock) => void;
  onDelete: () => void;
  dragHandleProps?: DragHandleProps;
  mobileReorderProps?: MobileReorderProps;
}

export function CalloutBlockEditor({
  block,
  onChange,
  onDelete,
  dragHandleProps,
  mobileReorderProps,
}: CalloutBlockEditorProps) {
  const handleTitleChange = (title: string) => {
    onChange({ ...block, title });
  };

  const handleContentChange = (content: string) => {
    onChange({ ...block, content });
  };

  return (
    <div className="group flex gap-2 sm:gap-3 p-3 sm:p-4 border rounded-lg bg-card hover:border-primary/50 transition-colors">
      {/* Drag Handle - hidden on mobile */}
      <div
        className="hidden sm:flex items-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-3 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <MessageSquare className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Callout Box</span>
            <span className="sm:hidden">Callout</span>
          </Label>
          <div className="flex items-center gap-1">
            {/* Mobile reorder buttons */}
            <div className="flex sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveUp}
                disabled={mobileReorderProps?.isFirst}
                className="h-9 w-9"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveDown}
                disabled={mobileReorderProps?.isLast}
                className="h-9 w-9"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-9 w-9 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Editor Fields */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`callout-title-${block.id}`} className="text-xs text-muted-foreground">
              Title (optional)
            </Label>
            <Input
              id={`callout-title-${block.id}`}
              value={block.title || ""}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="e.g., Important Note..."
              className="text-sm sm:text-base"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`callout-content-${block.id}`} className="text-xs text-muted-foreground">
              Content
            </Label>
            <Textarea
              id={`callout-content-${block.id}`}
              value={block.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Enter callout content..."
              className="min-h-[60px] sm:min-h-[80px] resize-y text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Preview - hide on very small screens */}
        <div className="mt-3 sm:mt-4 hidden sm:block">
          <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
          <div className="border-l-4 border-primary bg-primary/5 p-3 sm:p-4 rounded-r-md">
            {block.title && (
              <p className="font-semibold text-primary mb-1 text-sm">{block.title}</p>
            )}
            <p className="text-sm text-foreground">
              {block.content || "Callout content will appear here..."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
